import { useTranslation } from "react-i18next";
import {
  CalendarCheck,
  CheckCircle,
  Money,
  TrendDown,
  TrendUp,
  Users,
  Wallet,
  type Icon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

function Trend({ change }: { change: number | null | undefined }) {
  if (change == null) return null;
  const positive = change >= 0;
  const TrendIcon = positive ? TrendUp : TrendDown;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
        positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700",
      )}
    >
      <TrendIcon className="size-3" />
      {positive ? "+" : ""}
      {change.toFixed(1)}%
    </span>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  change,
  foot,
  hero,
}: {
  icon: Icon;
  label: string;
  value: string;
  change?: number | null;
  foot?: string;
  hero?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative flex min-h-28 flex-col gap-3 overflow-hidden rounded-2xl border p-4 shadow-sm sm:p-5",
        hero
          ? "col-span-2 border-primary/20 bg-primary/10 md:col-span-3 lg:col-span-1"
          : "border-border/60 bg-card",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={cn(
            "inline-flex size-10 shrink-0 items-center justify-center rounded-xl",
            hero ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
          )}
        >
          <Icon className="size-4" weight="duotone" />
        </span>
        <Trend change={change} />
      </div>
      <div className="min-w-0">
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        <p
          className={cn("mt-2 font-semibold leading-none tabular-nums text-foreground", hero ? "text-[28px]" : "text-2xl")}
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {value}
        </p>
      </div>
      {foot && (
        <p className="mt-auto text-xs text-muted-foreground">{foot}</p>
      )}
    </div>
  );
}

export function AdminKpiBand({
  totalRevenue,
  revenueChange,
  totalSessions,
  sessionsChange,
  completedSessions,
  completedChange,
  completionRate,
  newClientsInRange,
  pendingPayouts,
  pendingPayoutSessionCount,
  formatCurrency,
}: {
  totalRevenue: number;
  revenueChange: number | null;
  totalSessions: number;
  sessionsChange: number | null;
  completedSessions: number;
  completedChange: number | null;
  completionRate: number;
  newClientsInRange: number;
  pendingPayouts: number;
  pendingPayoutSessionCount: number;
  formatCurrency: (value: number) => string;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-[1.5fr_repeat(4,1fr)]">
      <Kpi
        hero
        icon={Money}
        label={t("admin.dashboard.revenueInPeriod")}
        value={formatCurrency(totalRevenue)}
        change={revenueChange}
      />
      <Kpi
        icon={CheckCircle}
        label={t("admin.dashboard.completedSessions")}
        value={String(completedSessions)}
        change={completedChange}
        foot={`${completionRate}% ${t("admin.dashboard.completionRate")}`}
      />
      <Kpi
        icon={CalendarCheck}
        label={t("admin.dashboard.totalSessions")}
        value={String(totalSessions)}
        change={sessionsChange}
      />
      <Kpi
        icon={Users}
        label={t("admin.dashboard.newClients")}
        value={String(newClientsInRange)}
      />
      <Kpi
        icon={Wallet}
        label={t("admin.dashboard.pendingPayouts")}
        value={formatCurrency(pendingPayouts)}
        foot={t("admin.dashboard.pendingPayoutSessions", { count: pendingPayoutSessionCount })}
      />
    </div>
  );
}
